import { translateField } from "@cdv/jazz-i18n";
import { useRouterParams } from "@cdv/jazz-ui";
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { ReactElement, useContext, useState } from "react";
import { useNavigate } from "react-router";
import { PageContext } from "../App/Context/PageContext";
import AppBar from "../ui/AppBar";
import { Resource } from "../utils";
import { QuestionCategory, TestResult } from "../../api";
import ResultCard from "./ResultCard";
import ResultCardCategory from "./ResultCardCategory";

type Answer = {
  questionId: string;
  answerId: string;
  points: number;
  reverseScoring: boolean;
  category: QuestionCategory | null;
};

export default function CardShow(): ReactElement {
  const { tests } = useContext(PageContext);
  const { id } = useRouterParams();
  const navigate = useNavigate();

  const version = tests
    ?.find((t) => t.id === location.hash.slice(1))
    ?.versions?.find((v) => v.id === id);

  const [current, setCurrent] = useState(1);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [result, setResult] = useState<TestResult | null>(null);
  const [resultCategory, setResultCategory] = useState<Record<QuestionCategory, number>>();

  const question = version?.questions?.sort((a, b) => a.order - b.order)[current - 1];

  if (!version || !question) return <></>;

  const minPoints = Math.min(...version.answers.map((a) => a.points));
  const maxPoints = Math.max(...version.answers.map((a) => a.points));

  const getPoints = (points: number) => {
    if (!question.reverseScoring) {
      return points;
    }

    return minPoints + maxPoints - points;
  };

  const selected = answers.find((a) => a.questionId === question.id);
  const last = current === version.questions.length;

  const calculateWithCategories = () => {
    const pointsByCategory: Record<QuestionCategory, number> = {
      [QuestionCategory.Depression]: 0,
      [QuestionCategory.Anxiety]: 0,
      [QuestionCategory.Stress]: 0,
    };

    for (const answer of answers) {
      if (answer.category) {
        pointsByCategory[answer.category] += answer.points;
      }
    }

    // DASS-21: vynásobenie skóre × 2
    for (const category of Object.keys(pointsByCategory) as QuestionCategory[]) {
      pointsByCategory[category] *= 2;
    }

    setResultCategory(pointsByCategory);
  };

  const calculate = () => {
    const points = answers.reduce((sum, a) => sum + a.points, 0);

    setResult(version.results?.find((r) => points >= r.minPoints && points <= r.maxPoints) ?? null);
  };

  return (
    <>
      <AppBar
        title={translateField(Resource.TestDefinition, "title")}
        onBack={() => {
          if (result || resultCategory) {
            setResult(null);
            navigate(-1);
          }
          current === 1 ? navigate(-1) : setCurrent((prev) => prev - 1);
        }}
      />

      <Stack
        sx={{
          minHeight: "79vh",
          mb: "60px",
        }}
      >
        <Card sx={{ mx: "16px", borderRadius: 0, boxShadow: "none" }}>
          <CardContent>
            {result || resultCategory ? (
              <>
                {result && <ResultCard version={version} result={result} />}
                {resultCategory && <ResultCardCategory version={version} result={resultCategory} />}
              </>
            ) : (
              <>
                <Typography variant="h5" sx={{ textAlign: "center", fontSize: "20px" }}>
                  {version.name}
                </Typography>

                <Typography sx={{ color: "#00000099" }}>{version.description}</Typography>

                <Stack sx={{ alignItems: "center", mt: "10px" }}>
                  <img src="/assets/ilu-test.svg" style={{ height: "100px" }} />
                </Stack>

                <Typography variant="h5" sx={{ textAlign: "center", mt: "10px", fontSize: "20px" }}>
                  {current} / {version.questions.length}
                </Typography>

                <Typography sx={{ color: "#000000DE" }}>{question.text}</Typography>

                <Stack sx={{ gap: 2, mt: "10px" }}>
                  {version.answers
                    ?.sort((a, b) => a.order - b.order)
                    .map((answer) => (
                      <FormControlLabel
                        key={answer.id}
                        label={answer.text}
                        control={
                          <Checkbox
                            checked={selected?.answerId === answer.id}
                            onChange={() =>
                              setAnswers((prev) => [
                                ...prev.filter((a) => a.questionId !== question.id),
                                {
                                  questionId: question.id,
                                  answerId: answer.id,
                                  points: getPoints(answer.points),
                                  reverseScoring: question.reverseScoring,
                                  category: question.category ?? null,
                                },
                              ])
                            }
                          />
                        }
                      />
                    ))}
                </Stack>
              </>
            )}
          </CardContent>
        </Card>

        <Stack sx={{ alignItems: "center", mb: 2, mt: "40px" }}>
          <Button
            variant="contained"
            size="large"
            color={last ? "warning" : "inherit"}
            disabled={!selected && !result && !resultCategory}
            onClick={() => {
              if (result || resultCategory) {
                navigate(-1);
              } else if (last) {
                if (question.category) {
                  calculateWithCategories();
                } else {
                  calculate();
                }
              } else {
                setCurrent((prev) => prev + 1);
              }
            }}
          >
            {result || resultCategory ? "OK" : last ? "Vyhodnotiť test" : "Pokračovat"}
          </Button>
        </Stack>
      </Stack>
    </>
  );
}
